import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { queryKeys } from './queryKeys';
import type { APIError } from '@/utils/api-client';
import { capTableService } from '@/services/capTableService';
import { documentService } from '@/services/documentService';
import { EntityType } from '@/types/document';
import type { PaginationParams } from '@/services/capTableService';
import type { TransactionActivityEntry } from '@/types/transaction-activity';
import {
  CapTableViewMode,
  CapTableFinality,
  type CapTableViewOptions,
  type CapTableExportOptions,
  type CreateStakeholderRequest,
  type UpdateStakeholderRequest,
  type CreateShareClassRequest,
  type CreateTransactionRequest,
  type CreateConvertibleRequest,
  type WarrantIssuanceRequest,
  type WarrantExerciseRequest,
  type CreateScenarioRequest,
  type ConvertToSharesAtMaturityRequest,
  type ExtendMaturityRequest,
  type AttachDocumentToTransactionRequest,
  type AddBankEntryRequest,
  type UpdateBankEntryRequest,
  type ShareClass,
  type DeriveConversionRequest,
  type CreateStockSplitRequest,
  type UpdateStockSplitRequest,
  type PreviewStockSplitRequest,
} from '@/types/captable';

/**
 * Wipe (not just invalidate) any cached round-edit payloads for a company.
 *
 * Status flips and row deletions change what the round-edit wizard renders
 * as locked vs editable. The wizard captures its initialFormState via
 * useState on mount, so React Query's default stale-while-revalidate would
 * let it render from cached data that no longer matches the server until
 * the user hard-refreshes. Removing the cache forces the next navigation
 * to wait for a fresh fetch (skeleton → wizard from fresh data).
 *
 * Call from any mutation that changes a row's status (approve, manual
 * approve, cancel asset sale, maturity conversion, repayment) or removes a
 * row from a round (delete transaction, delete convertible, delete asset
 * transaction).
 */
export function useDropRoundEditCacheForCompany() {
  const queryClient = useQueryClient();
  return (companyId: string) => {
    queryClient.removeQueries({
      queryKey: [...queryKeys.capTable.all, 'company', companyId, 'rounds'],
    });
  };
}

interface UseCapTableMatrixOptions {
  companyId: string;
  fromDate?: string;
  toDate?: string;
  viewMode?: CapTableViewMode;
  finality?: CapTableFinality;
  enabled?: boolean;
}

export const useCapTableMatrix = ({
  companyId,
  fromDate,
  toDate,
  viewMode = CapTableViewMode.FULLY_DILUTED,
  finality = CapTableFinality.WORKING,
  enabled = true,
}: UseCapTableMatrixOptions) => {
  // `params` is part of the query key below, so the two views cache separately
  // rather than evicting each other.
  const params = {
    ...(fromDate && { from_date: fromDate }),
    ...(toDate && { to_date: toDate }),
    view_mode: viewMode,
    finality,
  };

  return useApiQuery(
    [...queryKeys.capTable.company(companyId), 'matrix', params],
    () => capTableService.getMatrix(companyId, params),
    {
      enabled: enabled && !!companyId,
    }
  );
};

// Query hooks
export const useCapTableSnapshot = (
  companyId: string,
  options?: CapTableViewOptions
) => {
  return useApiQuery(
    [...queryKeys.capTable.all, 'snapshot', companyId, options],
    () => capTableService.getSnapshot(companyId, options),
    {
      enabled: !!companyId,
    }
  );
};

const useCapTableTransactions = (companyId: string, limit: number = 50) => {
  return useApiQuery(
    [...queryKeys.capTable.company(companyId), 'transactions', { limit }],
    () => capTableService.getTransactions(companyId, limit),
    {
      enabled: !!companyId,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

export const useCapTableTransactionsPaginated = (
  companyId: string,
  page: number = 1,
  pageSize: number = 50,
  enabled: boolean = true
) => {
  const params: PaginationParams = {
    page,
    page_size: pageSize,
  };

  return useApiQuery(
    [
      ...queryKeys.capTable.company(companyId),
      'transactions',
      'paginated',
      params,
    ],
    () => capTableService.getTransactionsPaginated(companyId, params),
    {
      enabled: enabled && !!companyId,
      placeholderData: keepPreviousData,
    }
  );
};

// Fund-page writes to the same shared log don't refresh this key: refetch on every mount.
export const useCapTableTransactionActivity = (
  companyId: string,
  transactionId: string | null | undefined
) =>
  useApiQuery<TransactionActivityEntry[]>(
    queryKeys.capTable.transactionActivity(companyId, transactionId as string),
    () =>
      capTableService.getTransactionActivity(
        companyId,
        transactionId as string
      ),
    { enabled: !!companyId && !!transactionId, staleTime: 0 }
  );

export const useCapTableTransaction = (
  companyId: string,
  transactionId: string
) => {
  return useApiQuery(
    [...queryKeys.capTable.company(companyId), 'transaction', transactionId],
    () => capTableService.getTransaction(companyId, transactionId),
    {
      enabled: !!companyId && !!transactionId,
    }
  );
};

export const useTransactionJournalEntries = (
  companyId: string,
  transactionId: string,
  enabled: boolean = true
) => {
  return useApiQuery(
    [
      ...queryKeys.capTable.company(companyId),
      'transaction',
      transactionId,
      'journal-entries',
    ],
    () =>
      capTableService.getTransactionJournalEntries(companyId, transactionId),
    {
      enabled: enabled && !!companyId && !!transactionId,
    }
  );
};

export const useStakeholders = (companyId: string) => {
  return useApiQuery(
    [...queryKeys.capTable.company(companyId), 'stakeholders'],
    () => capTableService.getStakeholders(companyId),
    {
      enabled: !!companyId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useShareClasses = (companyId: string) => {
  return useApiQuery(
    [...queryKeys.capTable.company(companyId), 'share-classes'],
    () => capTableService.getShareClasses(companyId),
    {
      enabled: !!companyId,
    }
  );
};

export const useConvertibles = (
  companyId: string,
  params?: { status?: string; as_of_date?: string }
) => {
  return useApiQuery(
    [...queryKeys.capTable.company(companyId), 'convertibles', params],
    () => capTableService.getConvertibles(companyId, params),
    {
      enabled: !!companyId,
    }
  );
};

export const useConvertible = (companyId: string, convertibleId: string) => {
  return useApiQuery(
    [...queryKeys.capTable.convertibles(companyId), 'detail', convertibleId],
    () => capTableService.getConvertibleDetail(companyId, convertibleId),
    {
      enabled: !!companyId && !!convertibleId,
    }
  );
};

// Note: useCapTableSummaryMetrics removed - endpoint not implemented in backend

export const useCapTableScenarios = (companyId: string) => {
  return useApiQuery(
    queryKeys.capTable.scenarios(companyId),
    () => capTableService.getScenarios(companyId),
    {
      enabled: !!companyId,
    }
  );
};

export const useCapTableScenario = (companyId: string, scenarioId: string) => {
  return useApiQuery(
    [...queryKeys.capTable.all, 'scenario', companyId, scenarioId],
    () => capTableService.getScenario(companyId, scenarioId),
    {
      enabled: !!companyId && !!scenarioId,
    }
  );
};

export const useMultipleScenarios = (
  companyId: string,
  scenarioIds: string[]
) => {
  return useApiQuery(
    [...queryKeys.capTable.all, 'scenarios', companyId, scenarioIds],
    () =>
      Promise.all(
        scenarioIds.map((id) => capTableService.getScenario(companyId, id))
      ),
    {
      enabled: !!companyId && scenarioIds.length > 0,
    }
  );
};

// Mutation hooks
// Calculates conversion terms (effective price, implied shares) without mutating.
// Toasts are handled at the call site so it can also patch form state on both paths.
export const useDeriveConversion = () => {
  return useApiMutation(
    ({
      companyId,
      convertibleId,
      data,
    }: {
      companyId: string;
      convertibleId: string;
      data: DeriveConversionRequest;
    }) => capTableService.deriveConversion(companyId, convertibleId, data),
    {
      showSuccessToast: false,
      showErrorToast: false,
    }
  );
};

export const useWaterfallAnalysis = () => {
  return useApiMutation(
    ({
      companyId,
      data,
    }: {
      companyId: string;
      data: { exit_value: number; scenario_id?: string };
    }) => capTableService.runWaterfallAnalysis(companyId, data),
    {
      showSuccessToast: false,
      showErrorToast: true,
    }
  );
};

// Transaction mutations
export const useCreateTransaction = () => {
  return useApiMutation(
    ({
      companyId,
      data,
    }: {
      companyId: string;
      data: CreateTransactionRequest;
    }) => capTableService.createTransaction(companyId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
        // Invalidate all snapshot queries for this company
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
        queryKeys.portfolio.detail(variables.companyId), // Transactions affect portfolio
      ],
      successMessage: 'Transaction created successfully',
    }
  );
};

export const useUpdateTransaction = () => {
  return useApiMutation(
    ({
      companyId,
      transactionId,
      data,
    }: {
      companyId: string;
      transactionId: string;
      data: Partial<CreateTransactionRequest>;
    }) => capTableService.updateTransaction(companyId, transactionId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
        // Invalidate all snapshot queries for this company
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
        queryKeys.portfolio.detail(variables.companyId),
      ],
      successMessage: 'Transaction updated successfully',
    }
  );
};

export const useDeleteTransaction = () => {
  const dropRoundEditCache = useDropRoundEditCacheForCompany();
  return useApiMutation(
    ({
      companyId,
      transactionId,
    }: {
      companyId: string;
      transactionId: string;
    }) => capTableService.deleteTransaction(companyId, transactionId),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
        // Invalidate all snapshot queries for this company
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
        queryKeys.portfolio.detail(variables.companyId),
      ],
      onSuccessCallback: (_data, variables) => {
        dropRoundEditCache(variables.companyId);
      },
      successMessage: 'Transaction deleted successfully',
    }
  );
};

// Stock split mutations
export const useCreateStockSplit = () => {
  return useApiMutation(
    ({
      companyId,
      data,
    }: {
      companyId: string;
      data: CreateStockSplitRequest;
    }) => capTableService.createStockSplit(companyId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
        queryKeys.portfolio.detail(variables.companyId),
      ],
      successMessage:
        'Stock split draft created — verify the legal doc to approve',
    }
  );
};

export const useUpdateStockSplit = () => {
  return useApiMutation(
    ({
      companyId,
      transactionId,
      data,
    }: {
      companyId: string;
      transactionId: string;
      data: UpdateStockSplitRequest;
    }) => capTableService.updateStockSplit(companyId, transactionId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
        queryKeys.portfolio.detail(variables.companyId),
      ],
      successMessage: 'Stock split updated successfully',
    }
  );
};

// The preview runs the same fold the save does, so the UI never re-derives
// shares or ratios locally. Params live in the query key, so a date, class,
// ratio or transaction_id change re-queries.
export const useStockSplitPreview = (
  companyId: string,
  params: PreviewStockSplitRequest,
  options?: { enabled?: boolean }
) => {
  return useApiQuery(
    queryKeys.capTable.splitPreview(companyId, params),
    () => capTableService.previewStockSplit(companyId, params),
    {
      enabled: (options?.enabled ?? true) && !!companyId,
    }
  );
};

// Stakeholder mutations
export const useCreateStakeholder = () => {
  return useApiMutation(
    ({
      companyId,
      data,
    }: {
      companyId: string;
      data: CreateStakeholderRequest;
    }) => capTableService.createStakeholder(companyId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
      ],
      successMessage: 'Stakeholder created successfully',
    }
  );
};

export const useUpdateStakeholder = () => {
  return useApiMutation(
    ({
      companyId,
      stakeholderId,
      data,
    }: {
      companyId: string;
      stakeholderId: string;
      data: UpdateStakeholderRequest;
    }) => capTableService.updateStakeholder(companyId, stakeholderId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
      ],
      successMessage: 'Stakeholder updated successfully',
    }
  );
};

export const useDeleteStakeholder = () => {
  return useApiMutation(
    ({
      companyId,
      stakeholderId,
    }: {
      companyId: string;
      stakeholderId: string;
    }) => capTableService.deleteStakeholder(companyId, stakeholderId),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
      ],
      successMessage: 'Stakeholder deleted successfully',
    }
  );
};

// Share class mutations
export const useCreateShareClass = () => {
  const queryClient = useQueryClient();
  return useApiMutation(
    ({
      companyId,
      data,
    }: {
      companyId: string;
      data: CreateShareClassRequest;
    }) => capTableService.createShareClass(companyId, data),
    {
      // Seed the list cache synchronously so consumers (e.g. ShareClassPicker)
      // can resolve the new id immediately, before the broader invalidation's
      // refetch returns.
      onSuccess: (createdShareClass, variables) => {
        const shareClassesKey = [
          ...queryKeys.capTable.company(variables.companyId),
          'share-classes',
        ];
        queryClient.setQueryData<ShareClass[]>(shareClassesKey, (existing) =>
          existing ? [...existing, createdShareClass] : [createdShareClass]
        );
      },
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
      ],
      successMessage: 'Share class created successfully',
    }
  );
};

export const useUpdateShareClass = () => {
  return useApiMutation(
    ({
      companyId,
      shareClassId,
      data,
    }: {
      companyId: string;
      shareClassId: string;
      data: Partial<CreateShareClassRequest>;
    }) => capTableService.updateShareClass(companyId, shareClassId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
      ],
      successMessage: 'Share class updated successfully',
    }
  );
};

export const useDeleteShareClass = () => {
  return useApiMutation(
    ({
      companyId,
      shareClassId,
    }: {
      companyId: string;
      shareClassId: string;
    }) => capTableService.deleteShareClass(companyId, shareClassId),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
      ],
      successMessage: 'Share class deleted successfully',
    }
  );
};

// Convertible mutations
export const useCreateConvertible = () => {
  return useApiMutation(
    ({
      companyId,
      data,
    }: {
      companyId: string;
      data: CreateConvertibleRequest;
    }) => capTableService.createConvertible(companyId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
        queryKeys.portfolio.detail(variables.companyId),
      ],
      successMessage:
        'Convertible draft created - verify the bank line to approve',
    }
  );
};

// Warrant mutations
export const useIssueWarrant = () => {
  return useApiMutation(
    ({
      companyId,
      data,
    }: {
      companyId: string;
      data: WarrantIssuanceRequest;
    }) => capTableService.issueWarrant(companyId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
        queryKeys.portfolio.detail(variables.companyId),
      ],
      successMessage: 'Warrant draft created - verify the legal doc to approve',
    }
  );
};

export const useExerciseWarrant = () => {
  return useApiMutation(
    ({
      companyId,
      instrumentId,
      data,
    }: {
      companyId: string;
      instrumentId: string;
      data: WarrantExerciseRequest;
    }) => capTableService.exerciseWarrant(companyId, instrumentId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
        queryKeys.portfolio.detail(variables.companyId),
      ],
      successMessage: 'Warrant exercise draft created - verify to approve',
    }
  );
};

export const useUpdateConvertible = () => {
  return useApiMutation(
    ({
      companyId,
      convertibleId,
      data,
    }: {
      companyId: string;
      convertibleId: string;
      data: Partial<CreateConvertibleRequest>;
    }) => capTableService.updateConvertible(companyId, convertibleId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
        queryKeys.portfolio.detail(variables.companyId),
        // The edit re-projects the DRAFT fund transaction (amount, date, and -
        // when the stakeholder changes - the owning fund), so scope the
        // invalidation to every fund rather than the one loaded before the edit.
        queryKeys.fundTransactions.all,
      ],
      successMessage: 'Convertible instrument updated successfully',
    }
  );
};

export const useDeleteConvertible = () => {
  const dropRoundEditCache = useDropRoundEditCacheForCompany();
  return useApiMutation(
    ({
      companyId,
      convertibleId,
    }: {
      companyId: string;
      convertibleId: string;
    }) => capTableService.deleteConvertible(companyId, convertibleId),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
        [...queryKeys.capTable.all, 'snapshot', variables.companyId],
        queryKeys.portfolio.detail(variables.companyId),
      ],
      onSuccessCallback: (_data, variables) => {
        dropRoundEditCache(variables.companyId);
      },
      successMessage: 'Convertible instrument deleted successfully',
    }
  );
};

// Maturity resolution hooks
export const useMaturityResolutionPreview = (
  companyId: string,
  instrumentId: string,
  pricePerShare?: number
) => {
  return useApiQuery(
    queryKeys.capTable.maturityPreview(companyId, instrumentId, pricePerShare),
    () =>
      capTableService.getMaturityResolutionPreview(
        companyId,
        instrumentId,
        pricePerShare
      ),
    {
      enabled: !!companyId && !!instrumentId,
    }
  );
};

export const useConvertToSharesAtMaturity = (companyId: string) => {
  const dropRoundEditCache = useDropRoundEditCacheForCompany();
  return useApiMutation(
    ({
      instrumentId,
      data,
    }: {
      instrumentId: string;
      data: ConvertToSharesAtMaturityRequest;
    }) =>
      capTableService.convertToSharesAtMaturity(companyId, instrumentId, data),
    {
      invalidateKeys: () => [
        queryKeys.capTable.convertibles(companyId),
        ['funds', 'detail'], // also refresh fund-level convertibles view
      ],
      onSuccessCallback: () => {
        dropRoundEditCache(companyId);
      },
      successMessage: 'Convertible note converted to shares successfully',
    }
  );
};

export const useExtendMaturity = (companyId: string) => {
  return useApiMutation(
    ({
      instrumentId,
      data,
    }: {
      instrumentId: string;
      data: ExtendMaturityRequest;
    }) => capTableService.extendMaturity(companyId, instrumentId, data),
    {
      invalidateKeys: () => [
        queryKeys.capTable.convertibles(companyId),
        ['funds', 'detail'], // also refresh fund-level convertibles view
      ],
      successMessage: 'Maturity date extended successfully',
    }
  );
};

export const useRepayInCash = (companyId: string) => {
  const dropRoundEditCache = useDropRoundEditCacheForCompany();
  return useApiMutation(
    (instrumentId: string) =>
      capTableService.repayInCash(companyId, instrumentId),
    {
      invalidateKeys: () => [
        queryKeys.capTable.convertibles(companyId),
        ['funds', 'detail'], // also refresh fund-level convertibles view
      ],
      onSuccessCallback: () => {
        dropRoundEditCache(companyId);
      },
      successMessage: 'Convertible note repaid successfully',
    }
  );
};

// Resolution document hooks
export const useConvertibleResolutionDocuments = (instrumentId: string) => {
  return useApiQuery(
    queryKeys.capTable.resolutionDocuments(instrumentId),
    () =>
      documentService.getEntityDocuments(
        EntityType.CONVERTIBLE_INSTRUMENT,
        instrumentId
      ),
    { enabled: !!instrumentId }
  );
};

export const useUploadResolutionDocument = () => {
  return useApiMutation(
    async ({
      file,
      companyId,
      instrumentId,
      contextType,
    }: {
      file: File;
      companyId: string;
      instrumentId: string;
      contextType:
        'maturity_conversion' | 'maturity_repayment' | 'maturity_extension';
    }) => {
      const { folder_id } = await documentService.getEntityFolder(
        'PORTFOLIO_COMPANY',
        companyId
      );
      return documentService.uploadDocument({
        file,
        collection_id: folder_id,
        entity_associations: [
          {
            entity_type: 'CONVERTIBLE_INSTRUMENT',
            entity_id: instrumentId,
            role: 'primary',
            context_type: contextType,
          },
        ],
      });
    },
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.resolutionDocuments(variables.instrumentId),
      ],
      showSuccessToast: false,
    }
  );
};

// Scenario mutations
export const useCreateScenario = () => {
  return useApiMutation(
    ({ companyId, data }: { companyId: string; data: CreateScenarioRequest }) =>
      capTableService.createScenario(companyId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.scenarios(variables.companyId),
      ],
      successMessage: 'Scenario created successfully',
    }
  );
};

export const useUpdateScenario = () => {
  return useApiMutation(
    ({
      companyId,
      scenarioId,
      data,
    }: {
      companyId: string;
      scenarioId: string;
      data: Partial<CreateScenarioRequest>;
    }) => capTableService.updateScenario(companyId, scenarioId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.scenarios(variables.companyId),
        queryKeys.capTable.scenario(variables.scenarioId),
      ],
      successMessage: 'Scenario updated successfully',
    }
  );
};

export const useDeleteScenario = () => {
  return useApiMutation(
    ({ companyId, scenarioId }: { companyId: string; scenarioId: string }) =>
      capTableService.deleteScenario(companyId, scenarioId),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.scenarios(variables.companyId),
      ],
      successMessage: 'Scenario deleted successfully',
    }
  );
};

// Export mutation
export const useExportCapTable = () => {
  return useApiMutation<
    Blob,
    APIError,
    {
      companyId: string;
      options: CapTableExportOptions;
    }
  >(
    ({ companyId, options }) =>
      capTableService.exportCapTable(companyId, options),
    {
      showSuccessToast: false, // We'll handle the download ourselves
      showErrorToast: true,
    }
  );
};

// Utility hook for CapTableSnapshotView
export const useCapTableSnapshotView = (companyId: string) => {
  const transactionsQuery = useCapTableTransactions(companyId);
  const stakeholdersQuery = useStakeholders(companyId);
  const shareClassesQuery = useShareClasses(companyId);

  return {
    transactions: transactionsQuery.data || [],
    stakeholders: stakeholdersQuery.data || [],
    shareClasses: shareClassesQuery.data || [],
    summaryMetrics: undefined, // Note: metrics endpoint not implemented in backend
    isLoading:
      transactionsQuery.isLoading ||
      stakeholdersQuery.isLoading ||
      shareClassesQuery.isLoading,
    isError:
      transactionsQuery.isError ||
      stakeholdersQuery.isError ||
      shareClassesQuery.isError,
    error:
      transactionsQuery.error ||
      stakeholdersQuery.error ||
      shareClassesQuery.error,
    refetchAll: () => {
      transactionsQuery.refetch();
      stakeholdersQuery.refetch();
      shareClassesQuery.refetch();
    },
  };
};

export const useCapTableSnapshotViewPaginated = (
  companyId: string,
  page: number = 1,
  pageSize: number = 50
) => {
  const transactionsQuery = useCapTableTransactionsPaginated(
    companyId,
    page,
    pageSize
  );

  return {
    transactions: transactionsQuery.data?.data || [],
    pagination: transactionsQuery.data?.pagination,
    isLoading: transactionsQuery.isLoading,
    isError: transactionsQuery.isError,
    error: transactionsQuery.error,
    refetchAll: () => {
      transactionsQuery.refetch();
    },
  };
};

// Transaction ↔ document linking
export const useTransactionDocuments = (
  companyId: string,
  transactionId: string,
  enabled: boolean = true
) => {
  return useApiQuery(
    queryKeys.capTable.transactionDocuments(companyId, transactionId),
    () => capTableService.getTransactionDocuments(companyId, transactionId),
    {
      enabled: enabled && !!companyId && !!transactionId,
    }
  );
};

export const useLinkDocumentToTransaction = () => {
  return useApiMutation(
    ({
      companyId,
      transactionId,
      data,
    }: {
      companyId: string;
      transactionId: string;
      data: AttachDocumentToTransactionRequest;
    }) =>
      capTableService.linkTransactionDocument(companyId, transactionId, data),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.transactionDocuments(
          variables.companyId,
          variables.transactionId
        ),
        queryKeys.capTable.transactionReconciliation(
          variables.companyId,
          variables.transactionId
        ),
        queryKeys.capTable.transactionActivity(
          variables.companyId,
          variables.transactionId
        ),
      ],
      successMessage: 'Document linked to transaction',
    }
  );
};

export const useUnlinkDocumentFromTransaction = () => {
  return useApiMutation(
    ({
      companyId,
      transactionId,
      linkId,
    }: {
      companyId: string;
      transactionId: string;
      linkId: string;
    }) =>
      capTableService.unlinkTransactionDocument(
        companyId,
        transactionId,
        linkId
      ),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.transactionDocuments(
          variables.companyId,
          variables.transactionId
        ),
        queryKeys.capTable.transactionReconciliation(
          variables.companyId,
          variables.transactionId
        ),
        queryKeys.capTable.transactionActivity(
          variables.companyId,
          variables.transactionId
        ),
      ],
      successMessage: 'Document unlinked',
    }
  );
};

export const useTransactionReconciliation = (
  companyId: string,
  transactionId: string,
  enabled: boolean = true
) => {
  return useApiQuery(
    queryKeys.capTable.transactionReconciliation(companyId, transactionId),
    () =>
      capTableService.getTransactionReconciliation(companyId, transactionId),
    {
      enabled: enabled && !!companyId && !!transactionId,
    }
  );
};

export const useTransactionBankEntries = (
  companyId: string,
  transactionId: string,
  enabled: boolean = true
) => {
  return useApiQuery(
    queryKeys.capTable.transactionBankEntries(companyId, transactionId),
    () => capTableService.listTransactionBankEntries(companyId, transactionId),
    {
      enabled: enabled && !!companyId && !!transactionId,
    }
  );
};

export const useTransactionFundTransactions = (
  companyId: string,
  transactionId: string
) => {
  return useApiQuery(
    queryKeys.capTable.transactionFundTransactions(companyId, transactionId),
    () =>
      capTableService.listTransactionFundTransactions(companyId, transactionId),
    {
      enabled: !!companyId && !!transactionId,
    }
  );
};

const bankEntryInvalidateKeys = (companyId: string, transactionId: string) => [
  queryKeys.capTable.transactionBankEntries(companyId, transactionId),
  queryKeys.capTable.transactionFundTransactions(companyId, transactionId),
  queryKeys.capTable.transactionReconciliation(companyId, transactionId),
  queryKeys.capTable.transactionActivity(companyId, transactionId),
];

export const useAddBankEntryToTransaction = () => {
  return useApiMutation(
    ({
      companyId,
      transactionId,
      data,
    }: {
      companyId: string;
      transactionId: string;
      data: AddBankEntryRequest;
    }) => capTableService.addBankEntry(companyId, transactionId, data),
    {
      invalidateKeys: (_data, variables) =>
        bankEntryInvalidateKeys(variables.companyId, variables.transactionId),
      successMessage: 'Bank entry added',
    }
  );
};

export const useUpdateBankEntry = () => {
  return useApiMutation(
    ({
      companyId,
      transactionId,
      entryId,
      data,
    }: {
      companyId: string;
      transactionId: string;
      entryId: string;
      data: UpdateBankEntryRequest;
    }) =>
      capTableService.updateBankEntry(companyId, transactionId, entryId, data),
    {
      invalidateKeys: (_data, variables) =>
        bankEntryInvalidateKeys(variables.companyId, variables.transactionId),
      successMessage: 'Bank entry updated',
    }
  );
};

export const useDeleteBankEntry = () => {
  return useApiMutation(
    ({
      companyId,
      transactionId,
      entryId,
    }: {
      companyId: string;
      transactionId: string;
      entryId: string;
    }) => capTableService.deleteBankEntry(companyId, transactionId, entryId),
    {
      invalidateKeys: (_data, variables) =>
        bankEntryInvalidateKeys(variables.companyId, variables.transactionId),
      successMessage: 'Bank entry removed',
    }
  );
};

// POC: admin-gated promotion mutation. Replaces the silent try_approve
// auto-flip: admin sees "Ready to approve" when threshold met and
// explicitly confirms.
export const useAdminApproveTransaction = () => {
  const dropRoundEditCache = useDropRoundEditCacheForCompany();
  return useApiMutation(
    ({
      companyId,
      transactionId,
    }: {
      companyId: string;
      transactionId: string;
    }) => capTableService.adminApproveTransaction(companyId, transactionId),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
        [
          ...queryKeys.capTable.company(variables.companyId),
          'transaction',
          variables.transactionId,
        ],
        queryKeys.capTable.transactionReconciliation(
          variables.companyId,
          variables.transactionId
        ),
        queryKeys.fundTransactions.all,
      ],
      onSuccessCallback: (_data, variables) => {
        dropRoundEditCache(variables.companyId);
      },
      successMessage: 'Transaction approved',
    }
  );
};

export const useManualApproveTransaction = () => {
  const dropRoundEditCache = useDropRoundEditCacheForCompany();
  return useApiMutation(
    ({
      companyId,
      transactionId,
      reason,
    }: {
      companyId: string;
      transactionId: string;
      reason: string;
    }) =>
      capTableService.manualApproveTransaction(companyId, transactionId, {
        reason,
      }),
    {
      invalidateKeys: (_data, variables) => [
        queryKeys.capTable.company(variables.companyId),
        [
          ...queryKeys.capTable.company(variables.companyId),
          'transaction',
          variables.transactionId,
        ],
        queryKeys.capTable.transactionReconciliation(
          variables.companyId,
          variables.transactionId
        ),
        queryKeys.fundTransactions.all,
      ],
      onSuccessCallback: (_data, variables) => {
        dropRoundEditCache(variables.companyId);
      },
      successMessage: 'Transaction approved',
    }
  );
};
